import { ExpandMore } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from '@mui/material'
import { ReactElement, ReactNode } from 'react'
import { useIsThemeV2 } from '@omnea-app/omui/NewThemeProvider'
import { usePagination } from '../../data/pagination/usePagination'
import { DefaultChip } from '../chip/DefaultChip'

type DialogInfoAccordionProps = {
  callouts: (string | React.ReactNode)[]
  title: string
  type: 'success' | 'error' | 'warning'
  icon: ReactElement<string>
  count?: number
  /** Optional element rendered beside the title (e.g. a deep-link). Clicks should not toggle the accordion. */
  titleAction?: ReactNode
}

export type CalloutProps = DialogInfoAccordionProps

export const DialogInfoAccordion = ({ title, callouts, icon, type, count, titleAction }: DialogInfoAccordionProps) => {
  const { itemsInCurrentPage, PaginationControls } = usePagination({ items: callouts, pageSize: 20 })
  const chipCount = count ?? callouts.length
  const isEmpty = callouts.length === 0
  const isThemeV2 = useIsThemeV2()
  return (
    <Box
      sx={
        isThemeV2
          ? {
              border: 'futureTheme.stroke.default',
              borderColor: 'futureTheme.border.strong',
              borderRadius: (theme) => theme.shape['futureTheme.radius-lg'],
            }
          : {
              border: 1,
              borderColor: 'neutralLight.300',
              backgroundColor: 'neutralLight.50',
              borderRadius: 1,
            }
      }
    >
      <Accordion
        expanded={isEmpty ? false : undefined}
        sx={isThemeV2 ? undefined : { backgroundColor: 'transparent', border: 'none' }}
      >
        <AccordionSummary
          expandIcon={isEmpty ? null : <ExpandMore />}
          sx={{
            minHeight: isThemeV2 ? undefined : '48px',
            maxHeight: isThemeV2 ? undefined : '48px',
            cursor: isEmpty ? 'default' : 'pointer',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" px={2}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="Body14pxMedium">{title}</Typography>
              {titleAction}
            </Stack>
            <Box>
              <DefaultChip label={chipCount.toString()} color={type} icon={icon} />
            </Box>
          </Stack>
        </AccordionSummary>
        {!isEmpty && (
          <AccordionDetails sx={isThemeV2 ? undefined : { p: '0px 16px 0px 38px' }}>
            <Box component="ul" p={isThemeV2 ? undefined : 0} mt={0} mb={isThemeV2 ? 0 : 3}>
              {itemsInCurrentPage.map((callout, index) => (
                <Typography key={index} variant="Body14pxRegular" component="li" sx={{ pb: 1 }}>
                  {callout}
                </Typography>
              ))}
            </Box>
            <PaginationControls hideIfSinglePage sx={isThemeV2 ? { mt: 3 } : { mb: 5, ml: -4 }} />
          </AccordionDetails>
        )}
      </Accordion>
    </Box>
  )
}
